import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Pencil, Trash2, Building } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/lib/supabase";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";

interface Supplier {
  id: number;
  name: string;
  contact_name: string;
  email: string;
  phone: string;
  address: string;
  street: string;
  number: string;
  neighborhood: string;
  city: string;
  created_at: string;
}

const Suppliers = () => {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<Supplier | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const { toast } = useToast();

  const [newSupplier, setNewSupplier] = useState({
    name: "",
    contact_name: "",
    email: "",
    phone: "",
    street: "",
    number: "",
    neighborhood: "",
    city: "",
  });

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const fetchSuppliers = async () => {
    try {
      const { data, error } = await supabase
        .from("suppliers")
        .select("*")
        .order("name", { ascending: true });

      if (error) throw error;
      setSuppliers(data || []);
    } catch (error) {
      console.error("Error fetching suppliers:", error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar os fornecedores",
        variant: "destructive",
      });
    }
  };

  const handleAddSupplier = async () => {
    if (!newSupplier.name.trim()) {
      toast({
        title: "Erro",
        description: "Por favor, digite o nome do fornecedor",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      // Construir o endereço completo a partir dos campos separados
      const fullAddress = [
        newSupplier.street.trim(),
        newSupplier.number.trim(),
        newSupplier.neighborhood.trim(),
        newSupplier.city.trim()
      ].filter(Boolean).join(', ');
      
      const { error } = await supabase
        .from("suppliers")
        .insert([{ 
          name: newSupplier.name.trim(),
          contact_name: newSupplier.contact_name.trim(),
          email: newSupplier.email.trim(),
          phone: newSupplier.phone.trim(),
          address: fullAddress,
          street: newSupplier.street.trim(),
          number: newSupplier.number.trim(),
          neighborhood: newSupplier.neighborhood.trim(),
          city: newSupplier.city.trim()
        }]);

      if (error) throw error;

      toast({
        title: "Sucesso",
        description: "Fornecedor adicionado com sucesso",
      });

      setNewSupplier({
        name: "",
        contact_name: "",
        email: "",
        phone: "",
        street: "",
        number: "",
        neighborhood: "",
        city: "",
      });
      setIsAddDialogOpen(false);
      fetchSuppliers();
    } catch (error) {
      console.error("Error adding supplier:", error);
      toast({
        title: "Erro",
        description: "Não foi possível adicionar o fornecedor",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditSupplier = async () => {
    if (!editingSupplier || !editingSupplier.name.trim()) {
      toast({
        title: "Erro",
        description: "Por favor, digite o nome do fornecedor",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      // Construir o endereço completo a partir dos campos separados
      const fullAddress = [
        editingSupplier.street?.trim() || "",
        editingSupplier.number?.trim() || "",
        editingSupplier.neighborhood?.trim() || "",
        editingSupplier.city?.trim() || ""
      ].filter(Boolean).join(', ');
      
      const { error } = await supabase
        .from("suppliers")
        .update({ 
          name: editingSupplier.name.trim(),
          contact_name: editingSupplier.contact_name?.trim() || "",
          email: editingSupplier.email?.trim() || "",
          phone: editingSupplier.phone?.trim() || "",
          address: fullAddress,
          street: editingSupplier.street?.trim() || "",
          number: editingSupplier.number?.trim() || "",
          neighborhood: editingSupplier.neighborhood?.trim() || "",
          city: editingSupplier.city?.trim() || ""
        })
        .eq("id", editingSupplier.id);

      if (error) throw error;

      toast({
        title: "Sucesso",
        description: "Fornecedor atualizado com sucesso",
      });

      setEditingSupplier(null);
      fetchSuppliers();
    } catch (error) {
      console.error("Error updating supplier:", error);
      toast({
        title: "Erro",
        description: "Não foi possível atualizar o fornecedor",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteSupplier = async (id: number) => {
    // Verificar se existem produtos usando este fornecedor
    const { data: products, error: checkError } = await supabase
      .from("products")
      .select("id")
      .eq("supplier_id", id)
      .limit(1);

    if (checkError) {
      console.error("Error checking products:", checkError);
      toast({
        title: "Erro",
        description: "Não foi possível verificar os produtos",
        variant: "destructive",
      });
      return;
    }

    if (products && products.length > 0) {
      toast({
        title: "Erro",
        description: "Este fornecedor não pode ser excluído porque está associado a produtos",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await supabase
        .from("suppliers")
        .delete()
        .eq("id", id);

      if (error) throw error;

      toast({
        title: "Sucesso",
        description: "Fornecedor excluído com sucesso",
      });

      fetchSuppliers();
    } catch (error) {
      console.error("Error deleting supplier:", error);
      toast({
        title: "Erro",
        description: "Não foi possível excluir o fornecedor",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const filteredSuppliers = suppliers.filter((supplier) =>
    supplier.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    supplier.contact_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    supplier.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    supplier.phone?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="container mx-auto py-6 max-w-7xl">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold tracking-tight">Fornecedores</h1>
        </div>

        {/* Stats Card */}
        <Card className="mb-6">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">
              Total de Fornecedores
            </CardTitle>
            <Building className="h-4 w-4 text-gray-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{suppliers.length}</div>
          </CardContent>
        </Card>

        {/* Search and Add */}
        <div className="flex items-center space-x-2 mb-6">
          <div className="flex-1">
            <Input
              placeholder="Buscar fornecedores..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full"
            />
          </div>
          <Button onClick={() => setIsAddDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Novo Fornecedor
          </Button>
        </div>

        {/* Suppliers Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="pl-6">Nome</TableHead>
                <TableHead>Contato</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Telefone</TableHead>
                <TableHead>Endereço</TableHead>
                <TableHead className="text-right pr-6">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredSuppliers.map((supplier) => (
                <TableRow key={supplier.id} className="hover:bg-gray-50">
                  <TableCell className="pl-6 py-4 font-medium">{supplier.name}</TableCell>
                  <TableCell>{supplier.contact_name || "-"}</TableCell>
                  <TableCell>{supplier.email || "-"}</TableCell>
                  <TableCell>{supplier.phone || "-"}</TableCell>
                  <TableCell>
                    <div className="text-sm">
                      {supplier.street && (
                        <div>
                          {supplier.street}{supplier.number ? `, ${supplier.number}` : ""}
                        </div>
                      )}
                      {(supplier.neighborhood || supplier.city) && (
                        <div className="text-gray-500">
                          {supplier.neighborhood}{supplier.city ? `, ${supplier.city}` : ""}
                        </div>
                      )}
                      {!supplier.street && !supplier.neighborhood && !supplier.city && "-"}
                    </div>
                  </TableCell>
                  <TableCell className="text-right pr-4">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditingSupplier(supplier)}
                        className="hover:bg-gray-100"
                      >
                        <Pencil className="h-4 w-4 text-gray-500" />
                        <span className="ml-2">Editar</span>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteSupplier(supplier.id)}
                        disabled={isLoading}
                        className="hover:bg-red-50 text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                        <span className="ml-2">Excluir</span>
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {filteredSuppliers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                    <div className="flex flex-col items-center justify-center">
                      <span className="text-lg">Nenhum fornecedor encontrado</span>
                      <span className="text-sm mt-1">
                        {searchTerm ? "Tente uma busca diferente" : "Comece adicionando um novo fornecedor"}
                      </span>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {/* Add Dialog */}
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold">Adicionar Fornecedor</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm font-medium text-gray-700">
                  Nome do Fornecedor *
                </Label>
                <Input
                  id="name"
                  placeholder="Digite o nome do fornecedor"
                  value={newSupplier.name}
                  onChange={(e) => setNewSupplier({...newSupplier, name: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact_name" className="text-sm font-medium text-gray-700">
                  Nome do Contato
                </Label>
                <Input
                  id="contact_name"
                  placeholder="Digite o nome do contato"
                  value={newSupplier.contact_name}
                  onChange={(e) => setNewSupplier({...newSupplier, contact_name: e.target.value})}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Digite o email"
                    value={newSupplier.email}
                    onChange={(e) => setNewSupplier({...newSupplier, email: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-sm font-medium text-gray-700">
                    Telefone
                  </Label>
                  <Input
                    id="phone"
                    placeholder="Digite o telefone"
                    value={newSupplier.phone}
                    onChange={(e) => setNewSupplier({...newSupplier, phone: e.target.value})}
                  />
                </div>
              </div>
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-gray-700">Endereço</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="street" className="text-sm font-medium text-gray-700">
                      Rua
                    </Label>
                    <Input
                      id="street"
                      placeholder="Digite a rua"
                      value={newSupplier.street}
                      onChange={(e) => setNewSupplier({...newSupplier, street: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="number" className="text-sm font-medium text-gray-700">
                      Número
                    </Label>
                    <Input
                      id="number"
                      placeholder="Digite o número"
                      value={newSupplier.number}
                      onChange={(e) => setNewSupplier({...newSupplier, number: e.target.value})}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="neighborhood" className="text-sm font-medium text-gray-700">
                      Bairro
                    </Label>
                    <Input
                      id="neighborhood"
                      placeholder="Digite o bairro"
                      value={newSupplier.neighborhood}
                      onChange={(e) => setNewSupplier({...newSupplier, neighborhood: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="city" className="text-sm font-medium text-gray-700">
                      Cidade
                    </Label>
                    <Input
                      id="city"
                      placeholder="Digite a cidade"
                      value={newSupplier.city}
                      onChange={(e) => setNewSupplier({...newSupplier, city: e.target.value})}
                    />
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setIsAddDialogOpen(false)}
              >
                Cancelar
              </Button>
              <Button onClick={handleAddSupplier} disabled={isLoading}>
                Adicionar Fornecedor
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={!!editingSupplier} onOpenChange={() => setEditingSupplier(null)}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold">Editar Fornecedor</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit_name" className="text-sm font-medium text-gray-700">
                  Nome do Fornecedor *
                </Label>
                <Input
                  id="edit_name"
                  placeholder="Digite o nome do fornecedor"
                  value={editingSupplier?.name || ""}
                  onChange={(e) =>
                    setEditingSupplier(
                      editingSupplier
                        ? { ...editingSupplier, name: e.target.value }
                        : null
                    )
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_contact_name" className="text-sm font-medium text-gray-700">
                  Nome do Contato
                </Label>
                <Input
                  id="edit_contact_name"
                  placeholder="Digite o nome do contato"
                  value={editingSupplier?.contact_name || ""}
                  onChange={(e) =>
                    setEditingSupplier(
                      editingSupplier
                        ? { ...editingSupplier, contact_name: e.target.value }
                        : null
                    )
                  }
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit_email" className="text-sm font-medium text-gray-700">
                    Email
                  </Label>
                  <Input
                    id="edit_email"
                    type="email"
                    placeholder="Digite o email"
                    value={editingSupplier?.email || ""}
                    onChange={(e) =>
                      setEditingSupplier(
                        editingSupplier
                          ? { ...editingSupplier, email: e.target.value }
                          : null
                      )
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit_phone" className="text-sm font-medium text-gray-700">
                    Telefone
                  </Label>
                  <Input
                    id="edit_phone"
                    placeholder="Digite o telefone"
                    value={editingSupplier?.phone || ""}
                    onChange={(e) =>
                      setEditingSupplier(
                        editingSupplier
                          ? { ...editingSupplier, phone: e.target.value }
                          : null
                      )
                    }
                  />
                </div>
              </div>
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-gray-700">Endereço</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit_street" className="text-sm font-medium text-gray-700">
                      Rua
                    </Label>
                    <Input
                      id="edit_street"
                      placeholder="Digite a rua"
                      value={editingSupplier?.street || ""}
                      onChange={(e) =>
                        setEditingSupplier(
                          editingSupplier
                            ? { ...editingSupplier, street: e.target.value }
                            : null
                        )
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit_number" className="text-sm font-medium text-gray-700">
                      Número
                    </Label>
                    <Input
                      id="edit_number"
                      placeholder="Digite o número"
                      value={editingSupplier?.number || ""}
                      onChange={(e) =>
                        setEditingSupplier(
                          editingSupplier
                            ? { ...editingSupplier, number: e.target.value }
                            : null
                        )
                      }
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit_neighborhood" className="text-sm font-medium text-gray-700">
                      Bairro
                    </Label>
                    <Input
                      id="edit_neighborhood"
                      placeholder="Digite o bairro"
                      value={editingSupplier?.neighborhood || ""}
                      onChange={(e) =>
                        setEditingSupplier(
                          editingSupplier
                            ? { ...editingSupplier, neighborhood: e.target.value }
                            : null
                        )
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit_city" className="text-sm font-medium text-gray-700">
                      Cidade
                    </Label>
                    <Input
                      id="edit_city"
                      placeholder="Digite a cidade"
                      value={editingSupplier?.city || ""}
                      onChange={(e) =>
                        setEditingSupplier(
                          editingSupplier
                            ? { ...editingSupplier, city: e.target.value }
                            : null
                        )
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setEditingSupplier(null)}
              >
                Cancelar
              </Button>
              <Button onClick={handleEditSupplier} disabled={isLoading}>
                Salvar Alterações
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Suppliers;
